import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { mobileAdmin } from '@/lib/mobileAdminPaths';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { PaginationBar } from '@/components/ui/PaginationBar';
import {
  inputClass,
  labelClass,
  inlineSuccessClass,
  inlineErrorClass,
} from '@/components/ui/inputStyles';
import { cn } from '@/lib/cn';

const PER_PAGE = 12;

type UserRow = {
  id: number;
  username: string;
  role: string;
  owned_sites_count: number;
  account_enabled: boolean;
  created_at: string;
  account_valid_until?: string | null;
  max_owned_sites?: number;
};

type ListResp = {
  ok: boolean;
  items: UserRow[];
  total: number;
  page: number;
  per_page: number;
};

const bigInput = cn(inputClass, 'py-3.5 text-base min-h-[52px]');
const bigLabel = 'mb-2 block text-sm font-semibold text-slate-700';

export function MobileUsersPage() {
  const { capabilities, loading: sessLoading } = useSession();
  const [items, setItems] = useState<UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [newUser, setNewUser] = useState('');
  const [newPass, setNewPass] = useState('');
  const [newValidUntil, setNewValidUntil] = useState('');
  const [newMaxSites, setNewMaxSites] = useState('20');
  const [addSaving, setAddSaving] = useState(false);
  const [validDraft, setValidDraft] = useState<Record<number, string>>({});
  const [maxSitesDraft, setMaxSitesDraft] = useState<Record<number, string>>({});

  const load = useCallback(
    async (override?: { page?: number; q?: string }) => {
      const p = override?.page ?? page;
      const qRaw = override?.q !== undefined ? override.q : appliedQ;
      const q = qRaw.trim();
      setLoading(true);
      setErr('');
      try {
        const r = await apiFetch<ListResp>('list_system_users', {
          method: 'GET',
          query: {
            page: p,
            per_page: PER_PAGE,
            q: q || undefined,
          },
        });
        if (r.ok) {
          const tp = Math.max(1, Math.ceil(r.total / r.per_page));
          const safePage = Math.min(p, tp);
          if (safePage !== p) {
            setPage(safePage);
            return;
          }
          setPage(safePage);
          setItems(r.items);
          setTotal(r.total);
        } else {
          setErr('加载失败');
        }
      } catch {
        setErr('加载失败');
      } finally {
        setLoading(false);
      }
    },
    [page, appliedQ]
  );

  useEffect(() => {
    if (sessLoading || !capabilities.manage_users) return;
    void load();
  }, [load, sessLoading, capabilities.manage_users]);

  function runSearch() {
    setAppliedQ(qInput.trim());
    setPage(1);
  }

  function openAddModal() {
    setNewUser('');
    setNewPass('');
    setNewValidUntil('');
    setNewMaxSites('20');
    setAddOpen(true);
  }

  async function submitCreateUser() {
    const u = newUser.trim();
    const pw = newPass.trim();
    if (pw !== '' && pw.length < 6) {
      setErr('初始密码至少 6 位，或留空使用默认 88888888');
      return;
    }
    let maxSites = parseInt(newMaxSites.trim(), 10);
    if (Number.isNaN(maxSites) || maxSites < 1) {
      setErr('站点数量上限须为 1～500 的整数');
      return;
    }
    if (maxSites > 500) {
      maxSites = 500;
    }
    setAddSaving(true);
    setMsg('');
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('create_system_user', {
        method: 'POST',
        body: JSON.stringify({
          username: u,
          max_owned_sites: maxSites,
          ...(pw !== '' ? { password: pw } : {}),
          ...(newValidUntil.trim() !== '' ? { account_valid_until: newValidUntil.trim() } : {}),
        }),
      });
      if (r.ok) {
        setMsg('已创建普通用户');
        setAddOpen(false);
        setAppliedQ('');
        setQInput('');
        await load({ page: 1, q: '' });
      } else {
        setErr(r.err === 'username_exists' ? '用户名已存在' : '创建失败');
      }
    } catch {
      setErr('请求失败');
    } finally {
      setAddSaving(false);
    }
  }

  async function toggleEnabled(id: number, enabled: boolean) {
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('update_system_user', {
        method: 'POST',
        body: JSON.stringify({ id, account_enabled: enabled }),
      });
      if (r.ok) {
        setMsg(enabled ? '已启用' : '已禁用');
        await load();
      } else {
        const m: Record<string, string> = {
          cannot_disable_self: '不能禁用当前登录账号',
          last_admin_required: '至少保留一名可用管理员',
        };
        setErr(m[r.err || ''] || '更新失败');
      }
    } catch {
      setErr('请求失败');
    }
  }

  async function resetPassword(id: number, username: string) {
    if (!window.confirm(`将用户「${username}」的密码重置为 88888888？`)) return;
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('reset_system_user_password', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      if (r.ok) {
        setMsg('已重置密码');
        await load();
      } else {
        const m: Record<string, string> = { invalid_target: '只能重置普通用户密码' };
        setErr(m[r.err || ''] || '重置失败');
      }
    } catch {
      setErr('请求失败');
    }
  }

  async function saveUserMaxSites(u: UserRow) {
    if (u.role !== 'user') return;
    setErr('');
    const raw = (maxSitesDraft[u.id] !== undefined ? maxSitesDraft[u.id]! : String(u.max_owned_sites ?? 20)).trim();
    let n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 1) {
      setErr('站点上限须为 1～500 的整数');
      return;
    }
    if (n > 500) {
      n = 500;
    }
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('update_system_user', {
        method: 'POST',
        body: JSON.stringify({ id: u.id, max_owned_sites: n }),
      });
      if (r.ok) {
        setMsg('已保存站点上限');
        setMaxSitesDraft((d) => {
          const x = { ...d };
          delete x[u.id];
          return x;
        });
        await load();
      } else {
        setErr('保存失败');
      }
    } catch {
      setErr('请求失败');
    }
  }

  async function saveUserValidUntil(u: UserRow) {
    if (u.role !== 'user') return;
    setErr('');
    const raw = validDraft[u.id] !== undefined ? validDraft[u.id]! : (u.account_valid_until ?? '');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('update_system_user', {
        method: 'POST',
        body: JSON.stringify({
          id: u.id,
          account_valid_until: raw.trim() === '' ? null : raw.trim(),
        }),
      });
      if (r.ok) {
        setMsg('已保存有效期');
        setValidDraft((d) => {
          const n = { ...d };
          delete n[u.id];
          return n;
        });
        await load();
      } else {
        setErr(r.detail || '保存失败');
      }
    } catch {
      setErr('请求失败');
    }
  }

  async function removeUser(id: number, username: string, role: string) {
    if (role === 'admin') {
      if (!window.confirm(`确定删除管理员「${username}」？不可恢复。`)) return;
    } else {
      if (!window.confirm(`确定删除用户「${username}」？不可恢复。`)) return;
    }
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('delete_system_user', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      if (r.ok) {
        setMsg('已删除');
        await load();
      } else {
        const m: Record<string, string> = {
          cannot_delete_self: '不能删除当前登录账号',
          last_admin_required: '至少保留一名管理员',
          user_has_sites: r.detail || '该用户仍拥有站点，请先删除其名下站点',
        };
        setErr(m[r.err || ''] || '删除失败');
      }
    } catch {
      setErr('请求失败');
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  if (!sessLoading && !capabilities.manage_users) {
    return <Navigate to={mobileAdmin.dashboard} replace />;
  }

  if (sessLoading) {
    return <Spinner label="验证权限" />;
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">用户管理</h1>
        <p className="mt-2 text-base leading-relaxed text-slate-600">
          创建与维护普通用户账号。站点与公众号等仍建议在电脑端配置。
        </p>
      </div>

      <Button type="button" variant="primary" className="min-h-14 w-full text-lg font-semibold" onClick={openAddModal}>
        新建普通用户
      </Button>

      <Card title="查询">
        <label className={labelClass}>
          <span className={bigLabel}>关键词</span>
          <input
            className={bigInput}
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runSearch()}
            placeholder="用户名 / ID / 角色"
            autoComplete="off"
          />
        </label>
        <div className="mt-3 flex gap-3">
          <Button type="button" variant="primary" className="min-h-12 flex-1 text-base" onClick={runSearch}>
            搜索
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="min-h-12 flex-1 text-base"
            onClick={() => {
              setQInput('');
              setAppliedQ('');
              setPage(1);
            }}
          >
            重置
          </Button>
        </div>
      </Card>

      {loading ? (
        <Spinner label="加载列表" />
      ) : (
        <div className="space-y-4">
          {items.length === 0 ? (
            <p className="rounded-2xl border border-slate-200/80 bg-white px-4 py-8 text-center text-base text-slate-500">
              暂无用户
            </p>
          ) : (
            items.map((u) => (
              <div
                key={u.id}
                className="rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm"
              >
                <div className="text-lg font-semibold text-slate-900">
                  {u.username}
                  <span className="ml-2 text-sm font-normal text-slate-500">
                    {u.role === 'admin' ? '管理员' : '普通用户'}
                    {u.account_enabled ? '' : ' · 已禁用'}
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  {u.role === 'user'
                    ? `站点 ${u.owned_sites_count} / 上限 ${u.max_owned_sites ?? 20} · ${
                        u.account_valid_until ? `有效期至 ${u.account_valid_until}` : '有效期不限'
                      }`
                    : '可管理全部站点'}
                </p>

                {u.role === 'user' ? (
                  <div className="mt-4 space-y-3 border-t border-slate-100 pt-4">
                    <div>
                      <span className={bigLabel}>站点数量上限（1～500）</span>
                      <div className="mt-1 flex items-stretch gap-2">
                        <input
                          type="number"
                          min={1}
                          max={500}
                          className={cn(bigInput, 'min-w-0 flex-1')}
                          value={
                            maxSitesDraft[u.id] !== undefined
                              ? maxSitesDraft[u.id]!
                              : String(u.max_owned_sites ?? 20)
                          }
                          onChange={(e) =>
                            setMaxSitesDraft((d) => ({
                              ...d,
                              [u.id]: e.target.value,
                            }))
                          }
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          className="shrink-0 self-stretch px-3 text-sm font-semibold"
                          onClick={() => void saveUserMaxSites(u)}
                        >
                          保存
                        </Button>
                      </div>
                    </div>
                    <div>
                      <span className={bigLabel}>有效期至</span>
                      <div className="mt-1 flex items-stretch gap-2">
                        <input
                          type="date"
                          className={cn(bigInput, 'min-w-0 flex-1')}
                          value={validDraft[u.id] !== undefined ? validDraft[u.id]! : (u.account_valid_until ?? '')}
                          onChange={(e) =>
                            setValidDraft((d) => ({
                              ...d,
                              [u.id]: e.target.value,
                            }))
                          }
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          className="shrink-0 self-stretch px-3 text-sm font-semibold"
                          onClick={() => void saveUserValidUntil(u)}
                        >
                          保存有效期
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        className="min-h-12 w-full px-2 text-sm font-semibold leading-tight"
                        title="清除有效期限制"
                        onClick={() => {
                          setValidDraft((d) => ({ ...d, [u.id]: '' }));
                          void (async () => {
                            setErr('');
                            try {
                              const r = await apiFetch<{ ok: boolean; err?: string }>('update_system_user', {
                                method: 'POST',
                                body: JSON.stringify({ id: u.id, account_valid_until: null }),
                              });
                              if (r.ok) {
                                setMsg('已设为不限');
                                setValidDraft((d) => {
                                  const n = { ...d };
                                  delete n[u.id];
                                  return n;
                                });
                                await load();
                              } else setErr('更新失败');
                            } catch {
                              setErr('请求失败');
                            }
                          })();
                        }}
                      >
                        清除限制
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        className="min-h-12 w-full px-2 text-sm font-semibold leading-tight"
                        title={u.account_enabled ? '禁用账号' : '启用账号'}
                        onClick={() => void toggleEnabled(u.id, !u.account_enabled)}
                      >
                        {u.account_enabled ? '禁用' : '启用'}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        className="min-h-12 w-full px-2 text-sm font-semibold leading-tight"
                        title="重置密码为 88888888"
                        onClick={() => void resetPassword(u.id, u.username)}
                      >
                        重置密码
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        className="min-h-12 w-full px-2 text-sm font-semibold leading-tight"
                        onClick={() => void removeUser(u.id, u.username, u.role)}
                      >
                        删除
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-slate-400">内置管理员请在数据库侧管理</p>
                )}
              </div>
            ))
          )}
          <PaginationBar
            page={page}
            totalPages={totalPages}
            total={total}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
            summary={total > 0 ? `共 ${total} 人` : undefined}
          />
        </div>
      )}

      <Modal
        open={addOpen}
        title="新建普通用户"
        onClose={() => !addSaving && setAddOpen(false)}
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" type="button" className="min-h-12 w-full sm:w-auto" disabled={addSaving} onClick={() => setAddOpen(false)}>
              取消
            </Button>
            <Button
              type="button"
              variant="primary"
              className="min-h-12 w-full sm:w-auto"
              disabled={addSaving || !newUser.trim() || (newPass.trim() !== '' && newPass.trim().length < 6)}
              onClick={() => void submitCreateUser()}
            >
              {addSaving ? '创建中…' : '创建'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">普通用户登录后自行创建站点。留空密码则为 88888888。</p>
          <label className={labelClass}>
            <span className={bigLabel}>用户名</span>
            <input
              className={bigInput}
              value={newUser}
              onChange={(e) => setNewUser(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <label className={labelClass}>
            <span className={bigLabel}>初始密码（可选，≥6 位）</span>
            <input
              type="password"
              className={bigInput}
              value={newPass}
              onChange={(e) => setNewPass(e.target.value)}
              {...(newPass.trim() !== '' ? { minLength: 6 } : {})}
              autoComplete="new-password"
            />
          </label>
          <label className={labelClass}>
            <span className={bigLabel}>有效期至（可选）</span>
            <input type="date" className={bigInput} value={newValidUntil} onChange={(e) => setNewValidUntil(e.target.value)} />
          </label>
          <label className={labelClass}>
            <span className={bigLabel}>站点数量上限（默认 20，最大 500）</span>
            <input
              type="number"
              min={1}
              max={500}
              className={bigInput}
              value={newMaxSites}
              onChange={(e) => setNewMaxSites(e.target.value)}
            />
          </label>
        </div>
      </Modal>

      {msg ? <p className={inlineSuccessClass}>{msg}</p> : null}
      {err ? <p className={inlineErrorClass}>{err}</p> : null}
    </div>
  );
}
